import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImagePlus, X } from 'lucide-react';
import { BACKEND_URL } from '@/utils/api';

export function SessionImageLibrary({
  open, onOpenChange, customLibrary, presetLibrary,
  onSelectImage, onDeleteFromLibrary, onSavePreset,
  onUploadToLibrary, uploadingToLibrary, newImageName, setNewImageName,
  getLibraryImageSrc
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito' }}>Choose a Skill Image</DialogTitle>
        </DialogHeader>

        {/* Custom Library */}
        {customLibrary.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-[#0E7490] uppercase tracking-wider mb-2">Your Library</p>
            <div className="grid grid-cols-4 gap-3">
              {customLibrary.map((img) => (
                <div key={img.entry_id} className="relative group">
                  <button
                    onClick={() => onSelectImage(img.is_url ? img.image_url : `${BACKEND_URL}/api/uploads/${img.image_url}`)}
                    className="w-full rounded-xl overflow-hidden border-2 border-transparent hover:border-[#22D3EE] transition-colors aspect-square"
                    data-testid={`custom-library-${img.entry_id}`}
                  >
                    <img src={getLibraryImageSrc(img)} alt={img.name} className="w-full h-full object-cover" />
                  </button>
                  <button
                    onClick={() => onDeleteFromLibrary(img.entry_id)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    data-testid={`delete-library-${img.entry_id}`}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                  <p className="text-[9px] text-center text-slate-500 mt-0.5 truncate">{img.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Default Presets */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Preset Images</p>
          <div className="grid grid-cols-4 gap-3">
            {presetLibrary.map((img) => {
              const alreadySaved = customLibrary.some(c => c.image_url === img.url);
              return (
                <div key={img.name} className="relative group">
                  <button
                    onClick={() => onSelectImage(img.url)}
                    className="w-full rounded-xl overflow-hidden border-2 border-transparent hover:border-[#22D3EE] transition-colors aspect-square"
                    data-testid={`library-image-${img.name}`}
                  >
                    <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                  </button>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[9px] text-slate-500 truncate">{img.name}</span>
                    {!alreadySaved ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onSavePreset(img.name, img.url); }}
                        className="text-[9px] text-[#0E7490] font-bold hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`save-preset-${img.name}`}
                      >
                        Save
                      </button>
                    ) : (
                      <span className="text-[9px] text-emerald-500 font-bold">Saved</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload to Library */}
        <div className="border-t border-slate-100 pt-3 mt-2">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Upload to Library</p>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Image name (optional)"
              value={newImageName}
              onChange={(e) => setNewImageName(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent outline-none"
              data-testid="library-image-name-input"
            />
          </div>
          <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-[#22D3EE] cursor-pointer transition-colors" data-testid="upload-to-library-btn">
            <ImagePlus className="w-5 h-5 text-slate-400" />
            <span className="text-sm text-slate-500">{uploadingToLibrary ? 'Uploading...' : 'Upload from device & save to library'}</span>
            <input type="file" accept="image/*" className="hidden" onChange={onUploadToLibrary} disabled={uploadingToLibrary} />
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
